import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { InternalPageHeader } from "@/components/InternalPageHeader";
import { forceStorageCleanupAndReload, storageAuditSummary } from "@/utils/storageMaintenance";
import { Upload, FileSpreadsheet, CheckCircle2, AlertCircle, RefreshCw, Tags, Shield, Users, Trash2, Activity, Key, Eye, EyeOff, AlertTriangle, FileText, X, Wifi, WifiOff, RotateCcw, Clock, Database, ShieldAlert, ChevronDown, ChevronUp, Download, Monitor, Zap, XCircle, Sun, Moon, UserPlus, Edit2, ToggleLeft, ToggleRight } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminPage({ onLogout, darkMode, toggleDarkMode }) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const tdsInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [replaceAll, setReplaceAll] = useState(false);
  
  // Security section state
  const [sessions, setSessions] = useState([]);
  const [loginAudit, setLoginAudit] = useState([]);
  const [stockAudit, setStockAudit] = useState([]);
  const [uploadAudit, setUploadAudit] = useState([]);
  const [stockIntegrityAudit, setStockIntegrityAudit] = useState(null);
  const [loadingSecurity, setLoadingSecurity] = useState(false);
  const [forcingCleanup, setForcingCleanup] = useState(false);
  
  // Password change state
  const [newStaffPassword, setNewStaffPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  
  // TDS section state
  const [tdsList, setTdsList] = useState([]);
  const [uploadingTds, setUploadingTds] = useState(false);

  // Sync Dashboard state
  const [pactSyncStatus, setPactSyncStatus] = useState(null);
  const [syncSnapshots, setSyncSnapshots] = useState([]);
  const [syncAudit, setSyncAudit] = useState([]);
  const [loadingSync, setLoadingSync] = useState(false);
  const [expandedSnapshot, setExpandedSnapshot] = useState(null);
  const [rollingBack, setRollingBack] = useState(false);

  // Phase 3/4: Enhanced pipeline status
  const [pipelineStatus, setPipelineStatus] = useState(null);
  const [triggeringRun, setTriggeringRun] = useState(false);

  // Salesman management state
  const [salesmen, setSalesmen] = useState([]);
  const [loadingSalesmen, setLoadingSalesmen] = useState(false);
  const [showSalesmanForm, setShowSalesmanForm] = useState(false);
  const [editingSalesman, setEditingSalesman] = useState(null);
  const [salesmanFormName, setSalesmanFormName] = useState("");
  const [salesmanFormEmail, setSalesmanFormEmail] = useState("");
  const [salesmanFormActive, setSalesmanFormActive] = useState(true);
  const [savingSalesman, setSavingSalesman] = useState(false);

  const getTdsAssignmentMeta = (tds) => {
    if (tds.assignment_status === "assigned") {
      return {
        label: "Matched",
        badgeClass: "bg-emerald-100 text-emerald-700",
        detail: tds.assigned_product_name
          ? `${tds.assigned_product_name}${tds.assigned_product_code ? ` · ${tds.assigned_product_code}` : ""}`
          : (tds.assigned_product_code || "Linked product"),
      };
    }

    return {
      label: "Unassigned",
      badgeClass: "bg-amber-100 text-amber-700",
      detail: "No filename match found",
    };
  };

  // Load security data on mount + auto-refresh pipeline every 30s
  useEffect(() => {
    loadSecurityData();
    loadTdsList();
    loadSyncData();
    loadPipelineStatus();
    loadAllSalesmen();
    const timer = setInterval(() => {
      loadPipelineStatus();
    }, 30000);
    return () => clearInterval(timer);
  }, []);

  const loadPipelineStatus = async () => {
    try {
      const res = await axios.get(`${API}/sync/pipeline-status`);
      // Multi-worker fallback: resolve best available timestamp
      const data = res.data;
      data._resolvedSyncTs = data.last_successful_sync || data.active_snapshot?.timestamp || null;
      setPipelineStatus(data);
    } catch (e) {
      // non-fatal
    }
  };

  const handleForceStorageCleanup = async () => {
    if (!window.confirm("Force client storage cleanup for this admin session and reload the app?")) return;
    setForcingCleanup(true);
    try {
      await forceStorageCleanupAndReload();
    } catch (error) {
      toast.error("Storage cleanup failed");
      setForcingCleanup(false);
    }
  };

  const handleRunNow = async () => {
    if (!window.confirm("Trigger a manual sync cycle? Bridge Agent must be running.")) return;
    setTriggeringRun(true);
    try {
      const res = await axios.post(`${API}/sync/run-now`, {}, {
        headers: { "X-Sync-Key": "redmagma-sync-2024" }
      });
      toast.success(`Trigger queued: ${res.data.trigger_id}`);
      setTimeout(loadPipelineStatus, 3000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Trigger failed");
    } finally {
      setTriggeringRun(false);
    }
  };
  
  const loadTdsList = async () => {
    try {
      const res = await axios.get(`${API}/tds`);
      setTdsList(res.data.tds_files || []);
    } catch (error) {
      console.error("Failed to load TDS list:", error);
    }
  };

  const loadSyncData = async () => {
    setLoadingSync(true);
    try {
      const [statusRes, snapsRes, auditRes] = await Promise.all([
        axios.get(`${API}/sync/status`),
        axios.get(`${API}/sync/snapshots`),
        axios.get(`${API}/sync/audit?limit=20`),
      ]);
      setPactSyncStatus(statusRes.data);
      setSyncSnapshots(snapsRes.data.snapshots || []);
      setSyncAudit(auditRes.data.audits || []);
    } catch (error) {
      console.error("Failed to load sync data:", error);
    } finally {
      setLoadingSync(false);
    }
  };

  const handleRollback = async (versionId) => {
    if (!window.confirm(`Rollback to snapshot ${versionId}? This will replace ALL current stock data.`)) return;
    setRollingBack(true);
    try {
      const res = await axios.post(`${API}/sync/rollback/${versionId}`, {}, {
        headers: { "X-Sync-Key": "redmagma-sync-2024" }
      });
      toast.success(`Rolled back to ${versionId}: ${res.data.items_restored} items restored`);
      loadSyncData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Rollback failed");
    } finally {
      setRollingBack(false);
    }
  };

  const handleExitSafeMode = async () => {
    if (!window.confirm("Exit Safe Mode? Sync ingestion will resume on next Bridge Agent cycle.")) return;
    try {
      await axios.post(`${API}/sync/exit-safe-mode`, {}, {
        headers: { "X-Sync-Key": "redmagma-sync-2024" }
      });
      toast.success("Safe Mode exited. Sync will resume.");
      loadSyncData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to exit safe mode");
    }
  };
  
  const handleTdsUpload = async (e) => {
    // Copy files immediately to a plain array — iOS Safari can lose FileList reference after re-render
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    for (let file of files) {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        toast.error(`${file.name} is not a PDF file`);
        if (tdsInputRef.current) tdsInputRef.current.value = "";
        return;
      }
    }

    setUploadingTds(true);

    try {
      const formData = new FormData();
      for (let file of files) {
        formData.append("files", file);
      }

      // Use fetch instead of axios — more reliable for multipart/FormData on iOS Safari
      const res = await fetch(`${API}/tds/upload-multiple`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Upload failed (${res.status})`);
      }

      const data = await res.json();

      if (data.uploaded && data.uploaded.length > 0) {
        const families = data.uploaded.map(u => u.product_family).join(", ");
        toast.success(`TDS uploaded for: ${families}`);
      }
      if (data.errors && data.errors.length > 0) {
        data.errors.forEach(err => toast.error(`${err.filename}: ${err.error}`));
      }

      loadTdsList();
    } catch (error) {
      toast.error(error.message || "Failed to upload TDS");
    } finally {
      setUploadingTds(false);
      if (tdsInputRef.current) tdsInputRef.current.value = "";
    }
  };
  
  const deleteTds = async (productFamily) => {
    if (!window.confirm(`Delete TDS for ${productFamily}?`)) return;
    
    try {
      await axios.delete(`${API}/tds/${encodeURIComponent(productFamily)}`);
      toast.success(`TDS deleted for ${productFamily}`);
      loadTdsList();
    } catch (error) {
      toast.error("Failed to delete TDS");
    }
  };

  const loadSecurityData = async () => {
    setLoadingSecurity(true);
    try {
      const [sessionsRes, auditRes, stockAuditRes, uploadAuditRes, integrityRes] = await Promise.all([
        axios.get(`${API}/admin/sessions`),
        axios.get(`${API}/admin/login-audit?limit=20`),
        axios.get(`${API}/admin/stock-audit?limit=10`),
        axios.get(`${API}/admin/upload-audit?limit=10`),
        axios.get(`${API}/admin/stock-integrity-audit?limit=12`),
      ]);
      setSessions(sessionsRes.data.sessions || []);
      setLoginAudit(auditRes.data.recent_logins || []);
      setStockAudit(stockAuditRes.data.changes || []);
      setUploadAudit(uploadAuditRes.data.uploads || []);
      setStockIntegrityAudit(integrityRes.data || null);
    } catch (error) {
      console.error("Failed to load security data:", error);
    } finally {
      setLoadingSecurity(false);
    }
  };

  const revokeAllStaffSessions = async () => {
    if (!window.confirm("Revoke ALL staff sessions? They will need to login again.")) return;
    
    try {
      const response = await axios.delete(`${API}/admin/sessions`);
      toast.success(response.data.message);
      loadSecurityData();
    } catch (error) {
      toast.error("Failed to revoke sessions");
    }
  };

  const changeStaffPassword = async () => {
    if (!newStaffPassword || newStaffPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    if (!window.confirm("Change staff password? All staff will be logged out and need to use the new password.")) return;
    
    setChangingPassword(true);
    try {
      const response = await axios.post(`${API}/admin/change-password`, {
        new_password: newStaffPassword
      });
      toast.success(response.data.message);
      setNewStaffPassword("");
      loadSecurityData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleFileUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append("files", files[i]);
    }

    setUploading(true);
    setUploadStatus(null);

    try {
      // Use different endpoint based on mode
      const endpoint = replaceAll 
        ? `${API}/stock/upload-multiple?mode=replace` 
        : `${API}/stock/upload-multiple?mode=update`;
      
      const response = await axios.post(endpoint, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        setUploadStatus({
          success: true,
          message: response.data.message,
          count: response.data.items_count,
          updated: response.data.updated_count,
          added: response.data.added_count,
        });
        toast.success(`✓ ${response.data.message}`);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Upload failed";
      setUploadStatus({
        success: false,
        message: errorMsg,
      });
      toast.error(errorMsg);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleSyncCategories = async () => {
    setSyncing(true);
    setSyncStatus(null);
    
    try {
      const response = await axios.post(`${API}/admin/sync-categories`);
      
      if (response.data.success) {
        setSyncStatus({
          success: true,
          message: response.data.message,
          total: response.data.total_products,
          updated: response.data.updated_count,
          updatedToKnown: response.data.updated_to_known_count || 0,
          correctedToUncategorized: response.data.corrected_to_uncategorized_count || 0,
        });
        toast.success(`✓ ${response.data.message}`);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Sync failed";
      setSyncStatus({
        success: false,
        message: errorMsg,
      });
      toast.error(errorMsg);
    } finally {
      setSyncing(false);
    }
  };

  // ==================== SALESMAN MANAGEMENT ====================
  const loadAllSalesmen = async () => {
    setLoadingSalesmen(true);
    try {
      const res = await axios.get(`${API}/admin/salesmen`);
      setSalesmen(res.data.salesmen || []);
    } catch (e) {
      toast.error("Failed to load salespeople");
    } finally {
      setLoadingSalesmen(false);
    }
  };

  const openAddSalesmanForm = () => {
    setEditingSalesman(null);
    setSalesmanFormName("");
    setSalesmanFormEmail("");
    setSalesmanFormActive(true);
    setShowSalesmanForm(true);
  };

  const handleEditSalesman = (s) => {
    setEditingSalesman(s);
    setSalesmanFormName(s.name || "");
    setSalesmanFormEmail(s.email || "");
    setSalesmanFormActive(s.active !== false);
    setShowSalesmanForm(true);
  };

  const handleSaveSalesman = async () => {
    if (!salesmanFormName.trim()) { toast.error("Name is required"); return; }
    setSavingSalesman(true);
    try {
      if (editingSalesman) {
        await axios.put(`${API}/salesmen/${editingSalesman.salesman_id}`, {
          name: salesmanFormName.trim(),
          email: salesmanFormEmail.trim() || undefined,
          active: salesmanFormActive,
        });
        toast.success(`${salesmanFormName} updated`);
      } else {
        await axios.post(`${API}/salesmen`, {
          name: salesmanFormName.trim(),
          email: salesmanFormEmail.trim() || undefined,
          active: salesmanFormActive,
        });
        toast.success(`${salesmanFormName} added`);
      }
      setShowSalesmanForm(false);
      setEditingSalesman(null);
      loadAllSalesmen();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save salesperson");
    } finally {
      setSavingSalesman(false);
    }
  };

  const handleToggleSalesman = async (s) => {
    const action = s.active ? "Deactivate" : "Activate";
    if (!window.confirm(`${action} ${s.name}?`)) return;
    try {
      await axios.put(`${API}/salesmen/${s.salesman_id}`, { active: !s.active });
      toast.success(`${s.name} ${s.active ? "deactivated" : "activated"}`);
      loadAllSalesmen();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    }
  };

  const handleHardDeleteSalesman = async (s) => {
    if (!window.confirm(`Permanently delete "${s.name}"? This cannot be undone.\n\nExisting reservations will remain linked.`)) return;
    try {
      await axios.delete(`${API}/admin/salesmen/${s.salesman_id}`);
      toast.success(`${s.name} deleted`);
      loadAllSalesmen();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F4F1] select-none w-full overflow-x-hidden">
      <InternalPageHeader
        title="ADMIN"
        subtitle="System controls, security, sync health, and file operations"
        onBack={() => navigate("/stock")}
        backTestId="admin-back-button"
        leading={<img src="/logo.jpeg" alt="Red Magma" className="w-8 h-8 object-contain rounded" />}
        actions={(
          <div className="flex items-center gap-1">
            <button
              data-testid="dark-mode-toggle"
              onClick={toggleDarkMode}
              className="p-2 rounded-lg text-white hover:bg-white/10 transition-colors"
              title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
            >
              {darkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <Button
              data-testid="master-ai-nav-btn"
              onClick={() => navigate("/master-ai")}
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/10 px-2 bg-white/5"
            >
              <span className="text-xs font-semibold">Pulse HQ</span>
            </Button>
          </div>
        )}
      />

      <main className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        <div className="bg-white rounded-xl shadow-[0_2px_10px_-3px_rgba(0,0,0,0.1)] p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#fef2f2] rounded-lg mb-4">
              <Upload className="h-8 w-8 text-[#DC2626]" />
            </div>
            <h2 className="heading-font text-3xl mb-2 text-[#09090b]">Upload Stock Data</h2>
            <p className="text-[#64748b]">
              Upload Excel (.xlsx, .xls), CSV, or PDF files. You can select multiple files at once.
            </p>
          </div>

          <div className="border-2 border-dashed border-[#e5e7eb] rounded-lg p-8 text-center hover:border-[#DC2626] transition-colors">
            {/* Upload Mode Switch */}
            <div className="flex items-center justify-center gap-3 mb-6 p-3 bg-gray-50 rounded-lg">
              <Label htmlFor="upload-mode" className={`text-sm font-medium ${!replaceAll ? 'text-[#DC2626]' : 'text-gray-500'}`}>
                Update Existing
              </Label>
              <Switch 
                id="upload-mode"
                checked={replaceAll}
                onCheckedChange={setReplaceAll}
                data-testid="upload-mode-switch"
              />
              <Label htmlFor="upload-mode" className={`text-sm font-medium ${replaceAll ? 'text-[#DC2626]' : 'text-gray-500'}`}>
                Replace All
              </Label>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              {replaceAll 
                ? "⚠️ Replace All: Deletes all existing stock and uploads fresh data"
                : "✓ Update Existing: Matches products by code and updates quantities, adds new products"
              }
            </p>
            
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls,.pdf"
              onChange={handleFileUpload}
              className="sr-only"
              id="file-upload"
              data-testid="file-upload-input"
              multiple
            />
            <label htmlFor="file-upload" className="cursor-pointer">
              <div className="mb-4">
                <FileSpreadsheet className="h-16 w-16 text-[#64748b] mx-auto" />
              </div>
              <Button
                data-testid="select-file-button"
                type="button"
                disabled={uploading}
                className="bg-[#DC2626] hover:bg-[#DC2626]/90 text-white font-semibold uppercase tracking-wide text-sm px-8 h-12 rounded-lg shadow-sm pointer-events-none"
              >
                {uploading ? "UPLOADING..." : "SELECT FILE"}
              </Button>
              <p className="text-sm text-[#64748b] mt-4">
                Supported formats: CSV, XLSX, XLS, PDF • Select multiple files
              </p>
            </label>
          </div>

          {uploadStatus && (
            <div
              className={`mt-6 p-4 rounded-lg border-l-4 ${
                uploadStatus.success
                  ? "bg-green-50 border-green-500"
                  : "bg-red-50 border-red-500"
              }`}
              data-testid="upload-status-message"
            >
              <div className="flex items-start gap-3">
                {uploadStatus.success ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                )}
                <div>
                  <p
                    className={`font-semibold ${
                      uploadStatus.success ? "text-green-800" : "text-red-800"
                    }`}
                  >
                    {uploadStatus.success ? "Success" : "Error"}
                  </p>
                  <p
                    className={`text-sm mt-1 ${
                      uploadStatus.success ? "text-green-700" : "text-red-700"
                    }`}
                  >
                    {uploadStatus.message}
                  </p>
                  {uploadStatus.success && (
                    <div className="text-sm text-green-600 mt-2 space-y-1">
                      {uploadStatus.count && (
                        <p>Total items processed: <span className="number-font font-semibold">{uploadStatus.count}</span></p>
                      )}
                      {uploadStatus.updated !== undefined && (
                        <p>Updated: <span className="number-font font-semibold">{uploadStatus.updated}</span></p>
                      )}
                      {uploadStatus.added !== undefined && (
                        <p>New items added: <span className="number-font font-semibold">{uploadStatus.added}</span></p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="mt-8 border-t pt-6">
            <h3 className="heading-font text-lg mb-4 text-[#09090b]">File Requirements</h3>
            <div className="space-y-2 text-sm text-[#64748b]">
              <p>• <strong>Required columns:</strong> Product Code, Description</p>
              <p>• <strong>Optional columns:</strong> Available Qty, Reserved Qty, Dye Lots, Unit, Category</p>
              <p>• <strong>Multiple files:</strong> Upload BL Stock + General Stock together</p>
              <p>• <strong>PDF Support:</strong> PDF must contain a clear table structure</p>
              <p>• Column names are flexible (e.g., "SKU" or "Product Code" both work)</p>
              <p>• <strong>Update mode:</strong> Matches products by code, updates existing, adds new</p>
              <p>• <strong>Replace mode:</strong> Clears all existing data and uploads fresh</p>
            </div>
          </div>
        </div>

        {/* Sync Categories Section */}
        <div className="bg-white rounded-xl shadow-[0_2px_10px_-3px_rgba(0,0,0,0.1)] p-6 sm:p-8 mt-6">
          <div className="flex flex-col sm:flex-row items-start gap-4">
            <div className="flex-shrink-0">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-[#fef9c3] rounded-lg">
                <Tags className="h-6 w-6 text-[#ca8a04]" />
              </div>
            </div>
            <div className="flex-1 w-full">
              <h3 className="heading-font text-xl mb-2 text-[#09090b]">Sync Product Categories</h3>
              <p className="text-sm text-[#64748b] mb-4">
                Re-sync product categories using the same authoritative catalog logic as stock ingestion.
                Known matches are categorized automatically, and unknown items stay <strong>Uncategorized</strong> instead of being guessed.
              </p>
              <Button
                data-testid="sync-categories-button"
                onClick={handleSyncCategories}
                disabled={syncing}
                className="bg-[#ca8a04] hover:bg-[#ca8a04]/90 text-white font-semibold uppercase tracking-wide text-sm px-6 h-10 rounded-lg"
              >
                {syncing ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    SYNCING...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    SYNC CATEGORIES
                  </>
                )}
              </Button>
              
              {syncStatus && (
                <div
                  className={`mt-4 p-3 rounded-lg ${
                    syncStatus.success
                      ? "bg-green-50 border border-green-200"
                      : "bg-red-50 border border-red-200"
                  }`}
                  data-testid="sync-status-message"
                >
                  <div className="flex items-center gap-2">
                    {syncStatus.success ? (
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-red-600" />
                    )}
                    <span className={`text-sm font-medium ${syncStatus.success ? "text-green-800" : "text-red-800"}`}>
                      {syncStatus.message}
                    </span>
                  </div>
                  {syncStatus.success && (
                    <div className="text-xs text-green-600 mt-1 ml-6 space-y-1" data-testid="sync-status-breakdown">
                      <p>{syncStatus.updated} of {syncStatus.total} products updated</p>
                      <p>{syncStatus.updatedToKnown} matched known categories · {syncStatus.correctedToUncategorized} corrected back to Uncategorized</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* TDS Upload Section */}
          <div className="bg-white rounded-xl shadow-[0_2px_10px_-3px_rgba(0,0,0,0.1)] p-6 sm:p-8 mt-6">
            <div className="flex flex-col sm:flex-row items-start gap-4">
              <div className="flex-shrink-0">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-50 rounded-lg">
                  <FileText className="h-6 w-6 text-blue-600" />
                </div>
              </div>
              <div className="flex-1 w-full">
                <h3 className="heading-font text-xl mb-2 text-[#09090b]">Technical Data Sheets (TDS)</h3>
                <p className="text-sm text-[#64748b] mb-4">
                  Upload TDS PDFs - product family is auto-detected from filename (e.g., <strong>SOLAR.pdf</strong> → SOLAR, <strong>VIP_TDS.pdf</strong> → VIP)
                </p>
                <p className="text-xs text-[#64748b] mb-4" data-testid="tds-assignment-help-text">
                  Uploads now show whether each TDS was auto-matched to a product from its filename. Unknown files stay unassigned.
                </p>
                
                {/* Upload new TDS - Bulk */}
                {/* Overlay pattern: transparent full-size input sits ON TOP of visible button — most reliable on iOS Safari */}
                <div className="relative w-full mb-4">
                  <div className={`w-full h-11 rounded-lg flex items-center justify-center text-sm font-semibold text-white ${uploadingTds ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600'}`}
                    data-testid="upload-tds-button">
                    {uploadingTds ? "Uploading..." : "Upload TDS PDFs (Select Multiple)"}
                  </div>
                  {!uploadingTds && (
                    <input
                      ref={tdsInputRef}
                      type="file"
                      accept=".pdf"
                      multiple
                      onChange={handleTdsUpload}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      style={{ fontSize: 0 }}
                    />
                  )}
                </div>
                
                <p className="text-xs text-gray-500 mb-4">
                  Name your files by product family: SOLAR.pdf, VIP.pdf, CORAL_datasheet.pdf, etc.
                </p>
                
                {/* TDS List */}
                {tdsList.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Uploaded TDS Files ({tdsList.length})
                    </div>
                    <div className="divide-y max-h-48 overflow-y-auto">
                      {tdsList.map((tds, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between gap-3 px-3 py-2 hover:bg-gray-50"
                          data-testid={`tds-list-row-${tds.product_family.toLowerCase()}`}
                        >
                          <div className="flex items-start gap-2 min-w-0">
                            <FileText className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="font-medium text-sm" data-testid={`tds-family-${tds.product_family.toLowerCase()}`}>{tds.product_family}</span>
                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${getTdsAssignmentMeta(tds).badgeClass}`} data-testid={`tds-assignment-badge-${tds.product_family.toLowerCase()}`}>
                                  {getTdsAssignmentMeta(tds).label}
                                </span>
                              </div>
                              <p className="text-xs text-gray-400 truncate" data-testid={`tds-filename-${tds.product_family.toLowerCase()}`}>{tds.filename}</p>
                              <p className="text-[11px] text-[#64748b] truncate" data-testid={`tds-assignment-detail-${tds.product_family.toLowerCase()}`}>
                                {getTdsAssignmentMeta(tds).detail}
                                {tds.matched_on ? ` · matched on ${tds.matched_on}` : ""}
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteTds(tds.product_family)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0"
                            data-testid={`delete-tds-${tds.product_family.toLowerCase()}`}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {tdsList.length === 0 && (
                  <p className="text-sm text-gray-400 italic">No TDS files uploaded yet</p>
                )}
              </div>
            </div>
          </div>

          {/* Security Section */}
          <div className="bg-white rounded-xl shadow-[0_5px_15px_-5px_rgba(0,0,0,0.08)] p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Shield className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h2 className="heading-font text-xl text-[#09090b]">Security</h2>
                <p className="text-sm text-[#64748b]">Monitor logins and manage sessions</p>
              </div>
            </div>

            {/* Active Sessions */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-[#64748b]" />
                  <span className="text-sm font-medium text-[#09090b]">Active Sessions</span>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                    {sessions.length} active
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={revokeAllStaffSessions}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  data-testid="revoke-all-sessions-button"
                >
                  <Trash2 className="h-3 w-3 mr-1" />
                  Revoke All
                </Button>
              </div>

              <div className="bg-gray-50 rounded-lg p-3 max-h-40 overflow-y-auto">
                {loadingSecurity ? (
                  <p className="text-sm text-[#64748b] text-center">Loading...</p>
                ) : sessions.length === 0 ? (
                  <p className="text-sm text-[#64748b] text-center">No active sessions</p>
                ) : (
                  <div className="space-y-2">
                    {sessions.map((session, idx) => (
                      <div key={idx} className="flex items-center justify-between text-xs bg-white p-2 rounded border">
                        <div>
                          <span className={`font-medium ${session.role === 'admin' ? 'text-purple-600' : 'text-blue-600'}`}>
                            {session.role?.toUpperCase()}
                          </span>
                          <span className="text-[#64748b] ml-2">{session.ip}</span>
                        </div>
                        <span className="text-[#94a3b8]">
                          {session.created_at ? new Date(session.created_at).toLocaleDateString() : 'N/A'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Recent Login Audit */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Activity className="h-4 w-4 text-[#64748b]" />
                <span className="text-sm font-medium text-[#09090b]">Recent Login Attempts</span>
                <Button variant="ghost" size="sm" onClick={loadSecurityData} className="ml-auto">
                  <RefreshCw className={`h-3 w-3 ${loadingSecurity ? 'animate-spin' : ''}`} />
                </Button>
              </div>

              <div className="bg-gray-50 rounded-lg p-3 max-h-48 overflow-y-auto">
                {loginAudit.length === 0 ? (
                  <p className="text-sm text-[#64748b] text-center">No recent logins</p>
                ) : (
                  <div className="space-y-1">
                    {loginAudit.map((log, idx) => (
                      <div key={idx} className={`flex items-center justify-between text-xs p-2 rounded ${log.success ? (log.is_new_ip ? 'bg-amber-50 border border-amber-200' : 'bg-green-50') : 'bg-red-50'}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`w-2 h-2 rounded-full ${log.success ? 'bg-green-500' : 'bg-red-500'}`}></span>
                          <span className="font-medium">{log.role || 'failed'}</span>
                          <span className="text-[#64748b]">{log.ip}</span>
                          {log.is_new_ip && log.success && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px] font-medium">
                              <AlertTriangle className="h-2.5 w-2.5" />
                              NEW IP
                            </span>
                          )}
                        </div>
                        <span className="text-[#94a3b8]">
                          {log.timestamp ? new Date(log.timestamp).toLocaleString() : 'N/A'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {loginAudit.some(l => !l.success) && (
                <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  Failed login attempts detected. Monitor for suspicious activity.
                </p>
              )}
              
              {loginAudit.some(l => l.is_new_ip && l.success) && (
                <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  Logins from new IP addresses detected. Verify these are legitimate users.
                </p>
              )}

              <div className="mt-4 rounded-lg border bg-slate-50 p-3" data-testid="storage-health-panel">
                <div className="flex items-center gap-2 mb-2">
                  <Monitor className="h-4 w-4 text-[#64748b]" />
                  <span className="text-sm font-medium text-[#09090b]">Storage Health Monitor</span>
                </div>
                <p className="text-xs text-[#64748b] mb-2">
                  Auto cleanup runs every 24 hours, deep cleanup every 7 days, and cache/build validation runs on every app load.
                </p>
                <div className="grid gap-2 md:grid-cols-2 text-xs text-[#475569] mb-3">
                  <div className="rounded bg-white border px-2 py-2">Build version: <strong>{storageAuditSummary.appBuildVersion}</strong></div>
                  <div className="rounded bg-white border px-2 py-2">Storage schema: <strong>{storageAuditSummary.appStorageVersion}</strong></div>
                </div>
                <Button
                  onClick={handleForceStorageCleanup}
                  disabled={forcingCleanup}
                  variant="outline"
                  className="border-slate-300"
                  data-testid="force-storage-cleanup-button"
                >
                  {forcingCleanup ? "Cleaning..." : "Force Storage Cleanup"}
                </Button>
              </div>
            </div>

            {/* Sensitive Action Audit */}
            <div className="mt-6 pt-6 border-t">
              <div className="flex items-center gap-2 mb-3">
                <ShieldAlert className="h-4 w-4 text-[#64748b]" />
                <span className="text-sm font-medium text-[#09090b]">Sensitive Action Audit</span>
                <span className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full" data-testid="sensitive-audit-count-badge">
                  {stockAudit.length + uploadAudit.length} recent actions
                </span>
              </div>
              <p className="text-xs text-[#64748b] mb-3">Admin-only visibility for recent stock mutations and upload operations.</p>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="bg-gray-50 rounded-lg p-3" data-testid="stock-audit-panel">
                  <p className="text-xs font-semibold text-[#09090b] mb-2">Recent Stock Changes</p>
                  {stockAudit.length === 0 ? (
                    <p className="text-xs text-[#64748b]">No recent stock changes</p>
                  ) : (
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {stockAudit.map((entry, idx) => (
                        <div key={idx} className="rounded bg-white border px-2 py-2 text-xs" data-testid={`stock-audit-row-${idx}`}>
                          <p className="font-medium text-[#09090b]">{entry.product_code} · {entry.update_type}</p>
                          <p className="text-[#64748b] mt-0.5">Qty changed: {entry.quantity_changed} {entry.batch_number ? `· Batch ${entry.batch_number}` : ''}</p>
                          <p className="text-[#94a3b8] mt-1">{entry.timestamp ? new Date(entry.timestamp).toLocaleString() : 'N/A'}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="bg-gray-50 rounded-lg p-3" data-testid="upload-audit-panel">
                  <p className="text-xs font-semibold text-[#09090b] mb-2">Recent Upload Activity</p>
                  {uploadAudit.length === 0 ? (
                    <p className="text-xs text-[#64748b]">No recent uploads</p>
                  ) : (
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {uploadAudit.map((entry, idx) => (
                        <div key={idx} className="rounded bg-white border px-2 py-2 text-xs" data-testid={`upload-audit-row-${idx}`}>
                          <p className="font-medium text-[#09090b]">{entry.mode?.toUpperCase() || 'UPLOAD'} · {entry.items_count || 0} items</p>
                          <p className="text-[#64748b] mt-0.5">Files: {(entry.file_names || []).join(', ') || 'N/A'}</p>
                          <p className="text-[#94a3b8] mt-1">{entry.timestamp ? new Date(entry.timestamp).toLocaleString() : 'N/A'}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Stock Integrity Audit */}
            <div className="mt-6 pt-6 border-t" data-testid="stock-integrity-audit-panel">
              <div className="flex items-center gap-2 mb-3">
                <Database className="h-4 w-4 text-[#64748b]" />
                <span className="text-sm font-medium text-[#09090b]">Stock Integrity Audit</span>
                <span className="text-xs bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full" data-testid="over-reserved-count-badge">
                  {stockIntegrityAudit?.over_reserved_count || 0} over-reserved items
                </span>
              </div>
              <p className="text-xs text-[#64748b] mb-3">
                Flags products where ERP reserved quantity is higher than available quantity, so quantity risks are visible instead of hidden.
              </p>
              {stockIntegrityAudit ? (
                <div className="space-y-3">
                  <div className="grid gap-3 md:grid-cols-3">
                    <div className="rounded-lg border bg-gray-50 px-3 py-3 text-xs">
                      <p className="text-[#64748b]">Total products scanned</p>
                      <p className="text-lg font-semibold text-[#09090b] mt-1">{stockIntegrityAudit.total_products}</p>
                    </div>
                    <div className="rounded-lg border bg-gray-50 px-3 py-3 text-xs">
                      <p className="text-[#64748b]">Over-reserved items</p>
                      <p className="text-lg font-semibold text-rose-600 mt-1">{stockIntegrityAudit.over_reserved_count}</p>
                    </div>
                    <div className="rounded-lg border bg-gray-50 px-3 py-3 text-xs">
                      <p className="text-[#64748b]">Active app reservations</p>
                      <p className="text-lg font-semibold text-[#09090b] mt-1">{stockIntegrityAudit.active_app_reservation_count}</p>
                    </div>
                  </div>

                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="rounded-lg border bg-white" data-testid="integrity-category-summary">
                      <div className="px-3 py-2 border-b bg-gray-50 text-xs font-semibold text-[#09090b]">Top affected categories</div>
                      <div className="divide-y max-h-48 overflow-y-auto">
                        {(stockIntegrityAudit.categories || []).filter(c => c.over_reserved_count > 0).slice(0, 8).map((category) => (
                          <div key={category.category} className="px-3 py-2 text-xs">
                            <div className="flex items-center justify-between gap-3">
                              <span className="font-medium text-[#09090b]">{category.category}</span>
                              <span className="text-rose-600 font-semibold">{category.over_reserved_count} items</span>
                            </div>
                            <p className="text-[#64748b] mt-1">Shortfall: {category.total_shortfall} · Available: {category.total_available} · Reserved: {category.total_reserved}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-lg border bg-white" data-testid="integrity-item-summary">
                      <div className="px-3 py-2 border-b bg-gray-50 text-xs font-semibold text-[#09090b]">Largest current shortfalls</div>
                      <div className="divide-y max-h-48 overflow-y-auto">
                        {(stockIntegrityAudit.items || []).slice(0, 8).map((item, idx) => (
                          <div key={`${item.product_code}-${idx}`} className="px-3 py-2 text-xs" data-testid={`integrity-item-row-${idx}`}>
                            <div className="flex items-center justify-between gap-3">
                              <span className="font-medium text-[#09090b]">{item.product_code}</span>
                              <span className="text-rose-600 font-semibold">-{item.shortfall_qty} {item.unit}</span>
                            </div>
                            <p className="text-[#64748b] mt-1 truncate">{item.description}</p>
                            <p className="text-[#94a3b8] mt-1">{item.category} · Avl {item.available_qty} · Res {item.reserved_qty}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-[#64748b]">Loading stock integrity audit…</p>
              )}
            </div>

            {/* Change Staff Password */}
            <div className="mt-6 pt-6 border-t">
              <div className="flex items-center gap-2 mb-3">
                <Key className="h-4 w-4 text-[#64748b]" />
                <span className="text-sm font-medium text-[#09090b]">Change Staff Password</span>
              </div>
              <p className="text-xs text-[#64748b] mb-3">
                Update the staff login password. All active staff sessions will be terminated.
              </p>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter new staff password"
                    value={newStaffPassword}
                    onChange={(e) => setNewStaffPassword(e.target.value)}
                    className="pr-10"
                    data-testid="staff-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <Button
                  onClick={changeStaffPassword}
                  disabled={changingPassword || !newStaffPassword || newStaffPassword.length < 6}
                  className="bg-[#DC2626] hover:bg-[#DC2626]/90 text-white"
                  data-testid="change-password-button"
                >
                  {changingPassword ? "Saving..." : "Save"}
                </Button>
              </div>
              {newStaffPassword && newStaffPassword.length < 6 && (
                <p className="text-xs text-red-500 mt-1">Password must be at least 6 characters</p>
              )}
            </div>
          </div>
        </div>

        {/* ===== AUTOMATION SCRIPTS DOWNLOAD ===== */}
        <div className="bg-white rounded-xl shadow-[0_2px_10px_-3px_rgba(0,0,0,0.1)] p-6 sm:p-8 mt-6">
          <div className="flex flex-col sm:flex-row items-start gap-4">
            <div className="flex-shrink-0">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-indigo-50 rounded-lg">
                <Monitor className="h-6 w-6 text-indigo-600" />
              </div>
            </div>
            <div className="flex-1 w-full min-w-0">
              <h3 className="heading-font text-xl mb-1 text-[#09090b]">PACT Sync Automation</h3>
              <p className="text-sm text-[#64748b] mb-4">
                Download the automation scripts to run on your Windows machine. These export stock data from PACT RevenU every 5 minutes and sync it here.
              </p>

              <a
                href="/downloads/pact_automation.zip"
                download="pact_automation.zip"
                data-testid="download-automation-btn"
                className="block"
              >
                <Button className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold uppercase tracking-wide text-sm w-full sm:w-auto px-6 h-10 rounded-lg">
                  <Download className="h-4 w-4 mr-2 shrink-0" />
                  Download Automation Scripts
                </Button>
              </a>

              <div className="mt-4 bg-indigo-50 rounded-lg p-4 text-sm text-indigo-800 space-y-1 overflow-hidden">
                <p className="font-semibold mb-2">Package includes:</p>
                <p className="break-all">• <code className="font-mono bg-white/70 px-1 rounded text-xs">pact_export.py</code> — UI automation to export from PACT RevenU</p>
                <p className="break-all">• <code className="font-mono bg-white/70 px-1 rounded text-xs">pact_ui_automation.py</code> — Robust window-focus safe click utilities</p>
                <p className="break-all">• <code className="font-mono bg-white/70 px-1 rounded text-xs">pact_launcher.py</code> — Auto-launch PACT if not running</p>
                <p className="break-all">• <code className="font-mono bg-white/70 px-1 rounded text-xs">bridge_agent.py</code> — Uploads exported files to this cloud app</p>
                <p className="break-all">• <code className="font-mono bg-white/70 px-1 rounded text-xs">RUNBOOK.md</code> — Full step-by-step setup guide</p>
              </div>

              <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 overflow-hidden">
                <p className="font-semibold mb-1">Setup (one-time):</p>
                <ol className="list-decimal ml-4 space-y-0.5">
                  <li>Extract zip to <code className="font-mono break-all">C:\PACT_EXPORTS\</code></li>
                  <li>Run: <code className="font-mono break-all">pip install -r requirements_windows.txt</code></li>
                  <li>Edit <code className="font-mono break-all">bridge_config.json</code> with your app URL</li>
                  <li>Run: <code className="font-mono break-all">python pact_export.py --verify</code> to calibrate click positions</li>
                  <li>Schedule both scripts in Windows Task Scheduler (see RUNBOOK.md)</li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        {/* ===== PACT SYNC DASHBOARD ===== */}
        <div className="bg-white rounded-xl shadow-[0_2px_10px_-3px_rgba(0,0,0,0.1)] p-6 sm:p-8 mt-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="inline-flex items-center justify-center w-10 h-10 bg-blue-50 rounded-lg">
                <Database className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h2 className="heading-font text-xl text-[#09090b]" data-testid="sync-dashboard-title">PACT Sync Dashboard</h2>
                <p className="text-xs text-[#64748b]">Live stock synchronization status</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={loadSyncData}
              disabled={loadingSync}
              data-testid="refresh-sync-btn"
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loadingSync ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {/* Safe Mode Alert */}
          {pactSyncStatus?.safe_mode && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-5" data-testid="safe-mode-alert">
              <div className="flex items-start gap-3">
                <ShieldAlert className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="font-semibold text-red-800 text-sm">SAFE MODE ACTIVE</p>
                  <p className="text-xs text-red-700 mt-1">Reason: {pactSyncStatus.safe_mode_reason}</p>
                  <p className="text-xs text-red-600 mt-0.5">Since: {new Date(pactSyncStatus.safe_mode_entered_at).toLocaleString()}</p>
                  <p className="text-xs text-red-600 mt-1">Ingestion paused. Serving last good snapshot. Manual review required.</p>
                  <Button
                    onClick={handleExitSafeMode}
                    size="sm"
                    className="mt-3 bg-red-600 hover:bg-red-700 text-white text-xs"
                    data-testid="exit-safe-mode-btn"
                  >
                    Exit Safe Mode
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Status Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <div className="bg-gray-50 rounded-lg p-3 text-center" data-testid="sync-status-connection">
              <div className="flex items-center justify-center gap-1.5 mb-1">
                {pactSyncStatus?.last_successful_sync ? (
                  <Wifi className="h-3.5 w-3.5 text-green-600" />
                ) : (
                  <WifiOff className="h-3.5 w-3.5 text-gray-400" />
                )}
                <span className="text-[10px] font-medium text-[#64748b] uppercase">Status</span>
              </div>
              <p className={`text-sm font-bold ${pactSyncStatus?.safe_mode ? 'text-red-600' : pactSyncStatus?.last_successful_sync ? 'text-green-600' : 'text-gray-400'}`}>
                {pactSyncStatus?.safe_mode ? 'SAFE MODE' : pactSyncStatus?.last_successful_sync ? 'CONNECTED' : 'WAITING'}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center" data-testid="sync-status-last-sync">
              <div className="flex items-center justify-center gap-1.5 mb-1">
                <Clock className="h-3.5 w-3.5 text-[#64748b]" />
                <span className="text-[10px] font-medium text-[#64748b] uppercase">Last Sync</span>
              </div>
              <p className="text-sm font-bold text-[#09090b]">
                {pactSyncStatus?.last_successful_sync 
                  ? new Date(pactSyncStatus.last_successful_sync).toLocaleTimeString() 
                  : 'Never'}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center" data-testid="sync-status-items">
              <div className="flex items-center justify-center gap-1.5 mb-1">
                <FileSpreadsheet className="h-3.5 w-3.5 text-[#64748b]" />
                <span className="text-[10px] font-medium text-[#64748b] uppercase">Items</span>
              </div>
              <p className="text-sm font-bold text-[#09090b]">
                {pactSyncStatus?.last_proof_pack?.row_count ?? '-'}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center" data-testid="sync-status-failures">
              <div className="flex items-center justify-center gap-1.5 mb-1">
                <AlertTriangle className="h-3.5 w-3.5 text-[#64748b]" />
                <span className="text-[10px] font-medium text-[#64748b] uppercase">Failures</span>
              </div>
              <p className={`text-sm font-bold ${pactSyncStatus?.consecutive_failures > 0 ? 'text-amber-600' : 'text-[#09090b]'}`}>
                {pactSyncStatus?.consecutive_failures ?? 0}
              </p>
            </div>
          </div>

          {/* Last Proof Pack */}
          {pactSyncStatus?.last_proof_pack && (
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-5" data-testid="sync-proof-pack">
              <p className="text-xs font-semibold text-blue-800 mb-2">Last Proof Pack</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <div><span className="text-blue-600">Version:</span> <span className="font-mono text-blue-900">{pactSyncStatus.last_proof_pack.version_id?.slice(0, 20)}</span></div>
                <div><span className="text-blue-600">Available:</span> <span className="font-bold text-blue-900">{pactSyncStatus.last_proof_pack.sum_available?.toLocaleString()}</span></div>
                <div><span className="text-blue-600">Reserved:</span> <span className="font-bold text-blue-900">{pactSyncStatus.last_proof_pack.sum_reserved?.toLocaleString()}</span></div>
                <div><span className="text-blue-600">Checksum:</span> <span className="font-mono text-blue-900">{pactSyncStatus.last_proof_pack.checksum}</span></div>
              </div>
            </div>
          )}

          {/* Snapshots */}
          {syncSnapshots.length > 0 && (
            <div className="mb-5">
              <p className="text-sm font-semibold text-[#09090b] mb-2 flex items-center gap-2">
                <Database className="h-4 w-4" /> Snapshots ({syncSnapshots.length})
              </p>
              <div className="space-y-2">
                {syncSnapshots.map((snap) => (
                  <div key={snap.version_id} className={`border rounded-lg overflow-hidden ${snap.status === 'active' ? 'border-green-200 bg-green-50/50' : 'border-gray-200'}`}>
                    <div
                      className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50/50"
                      onClick={() => setExpandedSnapshot(expandedSnapshot === snap.version_id ? null : snap.version_id)}
                      data-testid={`snapshot-${snap.version_id}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {snap.status === 'active' ? (
                          <CheckCircle2 className="h-3.5 w-3.5 text-green-600 shrink-0" />
                        ) : (
                          <Clock className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                        )}
                        <span className="text-xs font-mono truncate">{snap.version_id}</span>
                        {snap.status === 'active' && <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium">ACTIVE</span>}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xs text-[#64748b]">{snap.item_count} items</span>
                        {snap.status !== 'active' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); handleRollback(snap.version_id); }}
                            disabled={rollingBack}
                            className="text-xs h-7 px-2"
                            data-testid={`rollback-${snap.version_id}`}
                          >
                            <RotateCcw className="h-3 w-3 mr-1" />
                            Rollback
                          </Button>
                        )}
                        {expandedSnapshot === snap.version_id ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                      </div>
                    </div>
                    {expandedSnapshot === snap.version_id && (
                      <div className="px-3 pb-3 border-t text-xs space-y-1">
                        <p><span className="text-[#64748b]">Time:</span> {new Date(snap.timestamp).toLocaleString()}</p>
                        <p><span className="text-[#64748b]">Hash:</span> <span className="font-mono">{snap.file_hash?.slice(0, 20)}...</span></p>
                        {snap.reconciliation && (
                          <p><span className="text-[#64748b]">Reconciliation:</span> {snap.reconciliation.passed ? <span className="text-green-600 font-semibold">PASSED</span> : <span className="text-red-600 font-semibold">FAILED</span>}</p>
                        )}
                        {snap.file_proof_pack && (
                          <p><span className="text-[#64748b]">Totals:</span> Available={snap.file_proof_pack.sum_available?.toLocaleString()} | Reserved={snap.file_proof_pack.sum_reserved?.toLocaleString()}</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Sync History */}
          {syncAudit.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-[#09090b] mb-2 flex items-center gap-2">
                <Activity className="h-4 w-4" /> Recent Sync Activity
              </p>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {syncAudit.map((entry, idx) => (
                  <div key={idx} className="flex items-center justify-between text-xs py-1.5 px-2 rounded bg-gray-50" data-testid={`sync-audit-${idx}`}>
                    <div className="flex items-center gap-2">
                      {entry.status === 'SUCCESS' ? (
                        <CheckCircle2 className="h-3 w-3 text-green-600" />
                      ) : entry.status === 'ROLLBACK' ? (
                        <RotateCcw className="h-3 w-3 text-amber-600" />
                      ) : (
                        <AlertCircle className="h-3 w-3 text-red-600" />
                      )}
                      <span className="font-mono">{entry.version_id?.slice(0, 25)}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-[#64748b]">{entry.item_count} items</span>
                      <span className="text-[#64748b]">{new Date(entry.timestamp).toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!pactSyncStatus?.last_successful_sync && !loadingSync && syncAudit.length === 0 && (
            <div className="text-center py-6 text-[#64748b]" data-testid="sync-empty-state">
              <WifiOff className="h-8 w-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No sync activity yet</p>
              <p className="text-xs mt-1">The Bridge Agent will appear here once it starts sending data from PACT.</p>
            </div>
          )}
        </div>

        {/* ═══════════════════════════════════════════════════
            PHASE 3/4/5: PIPELINE STATUS PANEL (Admin Only)
            ════════════════════════════════════════════════ */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 mt-5" data-testid="pipeline-status-panel">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-[#DC2626]" />
              <span className="text-sm font-bold text-[#09090b]">Pipeline Status</span>
              {pipelineStatus?.is_critical && (
                <span data-testid="pipeline-critical-badge" className="px-2 py-0.5 bg-red-600 text-white text-[10px] font-bold rounded-full uppercase animate-pulse">CRITICAL</span>
              )}
              {pipelineStatus?.safe_mode && (
                <span data-testid="pipeline-safe-mode-badge" className="px-2 py-0.5 bg-red-100 text-red-700 text-[10px] font-bold rounded uppercase">SAFE MODE</span>
              )}
              {pipelineStatus?.consecutive_failures >= 2 && !pipelineStatus?.is_critical && !pipelineStatus?.safe_mode && (
                <span data-testid="pipeline-failure-badge" className="px-2 py-0.5 bg-amber-100 text-amber-800 text-[10px] font-semibold rounded uppercase">
                  {pipelineStatus.consecutive_failures} CONSEC. FAILURES
                </span>
              )}
              {pipelineStatus && !pipelineStatus.is_stale && !pipelineStatus.safe_mode && pipelineStatus.consecutive_failures === 0 && (
                <span data-testid="pipeline-ok-badge" className="px-2 py-0.5 bg-green-100 text-green-700 text-[10px] font-semibold rounded uppercase">OK</span>
              )}
              {pipelineStatus?.is_stale && !pipelineStatus?.safe_mode && (
                <span data-testid="pipeline-stale-badge" className="px-2 py-0.5 bg-amber-100 text-amber-800 text-[10px] font-semibold rounded uppercase">STALE</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={loadPipelineStatus}
                className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
                title="Refresh"
                data-testid="pipeline-refresh-btn"
              >
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={handleRunNow}
                disabled={triggeringRun}
                data-testid="pipeline-run-now-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#DC2626] text-white text-xs font-semibold rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {triggeringRun ? (
                  <>
                    <RefreshCw className="h-3 w-3 animate-spin" />
                    Triggering...
                  </>
                ) : (
                  <>
                    <Zap className="h-3 w-3" />
                    Run Now
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Key Metrics Row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-[10px] text-gray-500 uppercase font-medium mb-1">Last Attempt</p>
              <p className="text-xs font-semibold text-gray-800 truncate" data-testid="pipeline-last-attempt">
                {pipelineStatus?.heartbeat?.last_attempt
                  ? new Date(pipelineStatus.heartbeat.last_attempt).toLocaleTimeString()
                  : pipelineStatus?._resolvedSyncTs
                    ? new Date(pipelineStatus._resolvedSyncTs).toLocaleTimeString()
                    : '—'}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-[10px] text-gray-500 uppercase font-medium mb-1">Last Success</p>
              <p className={`text-xs font-semibold truncate ${pipelineStatus?.is_stale ? 'text-amber-600' : 'text-green-600'}`} data-testid="pipeline-last-success">
                {pipelineStatus?._resolvedSyncTs
                  ? new Date(pipelineStatus._resolvedSyncTs).toLocaleTimeString()
                  : '—'}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-[10px] text-gray-500 uppercase font-medium mb-1">Snapshot ID</p>
              <p className="text-xs font-mono text-gray-700 truncate" data-testid="pipeline-snapshot-id">
                {pipelineStatus?.active_snapshot?.version_id?.slice(-16) || pipelineStatus?.heartbeat?.last_snapshot_id?.slice(-16) || '—'}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-[10px] text-gray-500 uppercase font-medium mb-1">Consec. Failures</p>
              <p className={`text-sm font-bold ${(pipelineStatus?.consecutive_failures || 0) >= 2 ? 'text-red-600' : 'text-gray-800'}`} data-testid="pipeline-consec-failures">
                {pipelineStatus?.consecutive_failures ?? (pipelineStatus?.heartbeat?.consecutive_failures ?? 0)}
              </p>
            </div>
          </div>

          {/* Last Error */}
          {pipelineStatus?.heartbeat?.last_error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="pipeline-last-error">
              <p className="text-[10px] text-red-500 uppercase font-medium mb-0.5">Last Error</p>
              <p className="text-xs text-red-700 font-mono break-all">{pipelineStatus.heartbeat.last_error}</p>
            </div>
          )}

          {/* Safe mode reason */}
          {pipelineStatus?.safe_mode && pipelineStatus?.safe_mode_reason && (
            <div className="mb-4 p-3 bg-red-50 border border-red-300 rounded-lg" data-testid="pipeline-safe-mode-reason">
              <p className="text-[10px] text-red-600 uppercase font-medium mb-0.5">Safe Mode Reason</p>
              <p className="text-xs text-red-800">{pipelineStatus.safe_mode_reason}</p>
              {pipelineStatus.safe_mode_entered_at && (
                <p className="text-[10px] text-red-500 mt-1">
                  Entered: {new Date(pipelineStatus.safe_mode_entered_at).toLocaleString()}
                  {' — auto-exits after 30 min'}
                </p>
              )}
            </div>
          )}

          {/* Last 5 Cycle Summaries */}
          {pipelineStatus?.last_5_cycles?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Last 5 Cycles</p>
              <div className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="pipeline-last-5-cycles">
                {pipelineStatus.last_5_cycles.map((cycle, idx) => (
                  <div key={idx} className="flex items-center justify-between text-xs py-1.5 px-2.5 rounded-lg bg-gray-50 border border-gray-100">
                    <div className="flex items-center gap-2 min-w-0">
                      {cycle.status === 'SUCCESS' ? (
                        <CheckCircle2 className="h-3 w-3 text-green-600 shrink-0" />
                      ) : cycle.status === 'ANOMALY' ? (
                        <AlertTriangle className="h-3 w-3 text-amber-500 shrink-0" />
                      ) : cycle.status === 'ROLLBACK' ? (
                        <RotateCcw className="h-3 w-3 text-blue-500 shrink-0" />
                      ) : (
                        <XCircle className="h-3 w-3 text-red-500 shrink-0" />
                      )}
                      <span className={`font-semibold ${
                        cycle.status === 'SUCCESS' ? 'text-green-700' :
                        cycle.status === 'ANOMALY' ? 'text-amber-700' :
                        cycle.status === 'ROLLBACK' ? 'text-blue-700' : 'text-red-700'
                      }`}>{cycle.status}</span>
                      <span className="text-gray-500 font-mono truncate">{cycle.version_id?.slice(-16)}</span>
                      {cycle.anomaly_detected && (
                        <span className="px-1 py-0.5 bg-amber-100 text-amber-700 text-[9px] rounded font-bold">ANOMALY</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 shrink-0 ml-2">
                      <span className="text-gray-500">{cycle.item_count} items</span>
                      <span className="text-gray-400 hidden sm:inline">{new Date(cycle.timestamp).toLocaleTimeString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Daily Health Summary */}
          {pipelineStatus?.daily_health && (
            <div className="mt-4 pt-4 border-t border-gray-100" data-testid="pipeline-daily-health">
              <p className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Today's Health ({pipelineStatus.daily_health.date})</p>
              <div className="flex gap-4 text-xs text-gray-600">
                <span><span className="font-semibold text-gray-800">{pipelineStatus.daily_health.total_cycles}</span> total cycles</span>
                <span><span className="font-semibold text-green-700">{pipelineStatus.daily_health.successful_cycles}</span> succeeded</span>
                <span><span className="font-semibold text-red-600">{pipelineStatus.daily_health.failed_cycles}</span> failed</span>
                {pipelineStatus.daily_health.avg_duration_s > 0 && (
                  <span><span className="font-semibold text-gray-800">{pipelineStatus.daily_health.avg_duration_s}s</span> avg</span>
                )}
              </div>
            </div>
          )}

          {/* Empty state */}
          {!pipelineStatus && (
            <div className="text-center py-4 text-gray-400" data-testid="pipeline-no-data">
              <WifiOff className="h-6 w-6 mx-auto mb-1.5 opacity-40" />
              <p className="text-xs">No pipeline data yet. Bridge Agent not connected.</p>
            </div>
          )}
        </div>

        {/* ==================== SALESPEOPLE MANAGEMENT ==================== */}
        <div className="bg-white rounded-xl shadow-[0_2px_10px_-3px_rgba(0,0,0,0.1)] p-4 sm:p-6 mt-6" data-testid="salespeople-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <h2 className="text-base font-bold text-[#09090b]">Salespeople</h2>
                <p className="text-xs text-[#64748b]">Manage who can log in and create reservations</p>
              </div>
            </div>
            <Button
              data-testid="add-salesperson-btn"
              size="sm"
              onClick={openAddSalesmanForm}
              className="bg-[#DC2626] hover:bg-[#B91C1C] text-white gap-1"
            >
              <UserPlus className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Add Person</span>
              <span className="sm:hidden">Add</span>
            </Button>
          </div>

          {/* Add / Edit Form */}
          {showSalesmanForm && (
            <div className="mb-4 p-4 border border-[#E5E7EB] rounded-lg bg-gray-50" data-testid="salesman-form">
              <h3 className="text-sm font-semibold text-[#09090b] mb-3">
                {editingSalesman ? `Edit — ${editingSalesman.name}` : "New Salesperson"}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-600 mb-1 block">Full Name *</Label>
                  <Input
                    data-testid="salesman-name-input"
                    placeholder="e.g. Ahmed Al Mansouri"
                    value={salesmanFormName}
                    onChange={e => setSalesmanFormName(e.target.value)}
                    className="h-9 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-600 mb-1 block">Email (for email login)</Label>
                  <Input
                    data-testid="salesman-email-input"
                    placeholder="name@redmagmauae.com"
                    type="email"
                    value={salesmanFormEmail}
                    onChange={e => setSalesmanFormEmail(e.target.value)}
                    className="h-9 text-sm"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <Switch
                  id="salesman-active-switch"
                  checked={salesmanFormActive}
                  onCheckedChange={setSalesmanFormActive}
                  data-testid="salesman-active-switch"
                />
                <Label htmlFor="salesman-active-switch" className="text-sm">
                  {salesmanFormActive ? "Active — can log in and create reservations" : "Disabled — cannot log in"}
                </Label>
              </div>
              <div className="flex gap-2 mt-4">
                <Button
                  data-testid="save-salesman-btn"
                  size="sm"
                  onClick={handleSaveSalesman}
                  disabled={savingSalesman}
                  className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
                >
                  {savingSalesman ? "Saving..." : editingSalesman ? "Save Changes" : "Add Salesperson"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { setShowSalesmanForm(false); setEditingSalesman(null); }}
                  data-testid="cancel-salesman-btn"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Salesmen Table */}
          {loadingSalesmen ? (
            <p className="text-sm text-gray-400 text-center py-4">Loading...</p>
          ) : salesmen.length === 0 ? (
            <div className="text-center py-6 text-gray-400">
              <Users className="h-8 w-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No salespeople configured yet</p>
            </div>
          ) : (
            <div className="border border-[#E5E7EB] rounded-lg overflow-hidden" data-testid="salesmen-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-[#E5E7EB]">
                    <tr>
                      <th className="text-left p-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Name</th>
                      <th className="text-left p-3 font-semibold text-gray-600 text-xs uppercase tracking-wide hidden sm:table-cell">Email</th>
                      <th className="text-left p-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Status</th>
                      <th className="text-right p-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F3F4F6]">
                    {salesmen.map((s) => (
                      <tr key={s.salesman_id} className="hover:bg-gray-50 transition-colors" data-testid={`salesman-row-${s.salesman_id}`}>
                        <td className="p-3 font-medium text-[#09090b]">{s.name}</td>
                        <td className="p-3 text-[#64748b] hidden sm:table-cell">{s.email || <span className="text-gray-300">—</span>}</td>
                        <td className="p-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                            s.active !== false
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-100 text-gray-500"
                          }`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${s.active !== false ? "bg-green-500" : "bg-gray-400"}`}></span>
                            {s.active !== false ? "Active" : "Disabled"}
                          </span>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              data-testid={`edit-salesman-${s.salesman_id}`}
                              onClick={() => handleEditSalesman(s)}
                              className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                              title="Edit"
                            >
                              <Edit2 className="h-3.5 w-3.5" />
                            </button>
                            <button
                              data-testid={`toggle-salesman-${s.salesman_id}`}
                              onClick={() => handleToggleSalesman(s)}
                              className={`p-1.5 rounded transition-colors ${s.active !== false ? "hover:bg-amber-50 text-amber-500 hover:text-amber-700" : "hover:bg-green-50 text-green-500 hover:text-green-700"}`}
                              title={s.active !== false ? "Deactivate" : "Activate"}
                            >
                              {s.active !== false ? <ToggleRight className="h-3.5 w-3.5" /> : <ToggleLeft className="h-3.5 w-3.5" />}
                            </button>
                            <button
                              data-testid={`delete-salesman-${s.salesman_id}`}
                              onClick={() => handleHardDeleteSalesman(s)}
                              className="p-1.5 rounded hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors"
                              title="Delete permanently"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-3 py-2 bg-gray-50 border-t border-[#E5E7EB] text-xs text-[#64748b]">
                {salesmen.filter(s => s.active !== false).length} active · {salesmen.filter(s => s.active === false).length} disabled
              </div>
            </div>
          )}
        </div>

      </main>
    </div>
  );
}